import { FC, ReactNode, createContext, useCallback, useContext, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { useIsOfficeAvailable } from 'src/features/plugins/outlook/contexts/OfficeContext';

interface DialogContextType {
  openOfficeDialog: (
    url: string,
    options: Office.DialogOptions,
    onMessage?: (arg: { message: string; origin: string | undefined } | { error: number }) => void
  ) => void;
  closeDialog: () => void;
  isDialogOpen: boolean;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const useDialog = (): DialogContextType => {
  const context = useContext(DialogContext);
  const isOfficeAvailable = useIsOfficeAvailable();

  if (!context || !isOfficeAvailable) {
    return {
      openOfficeDialog: () => {
        // No-op: Office dialog functionality not available outside Outlook plugin
      },
      closeDialog: () => {
        // No-op: Office dialog functionality not available outside Outlook plugin
      },
      isDialogOpen: false,
    };
  }

  return context;
};

interface DialogProviderProps {
  children: ReactNode;
}

export const DialogProvider: FC<DialogProviderProps> = ({ children }) => {
  const officeDialogRef = useRef<Office.Dialog | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const dlgLog = (message: string, context?: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;
    datadogLogs.logger.info(message, { ...context, source: 'DialogContext' });
  };

  const dlgLogError = (message: string, context?: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;
    datadogLogs.logger.error(message, { ...context, source: 'DialogContext' });
  };

  const closeDialog = useCallback(() => {
    dlgLog('[closeDialog] closing dialog', { hasRef: !!officeDialogRef.current });
    if (officeDialogRef.current) {
      officeDialogRef.current.close();
      officeDialogRef.current = null;
      setIsDialogOpen(false);
    }
  }, []);

  const handleDialogEvent = useCallback((arg: { error: number; type?: Office.EventType }) => {
    dlgLog('[dialogEvent] received', { error: arg.error, type: String(arg.type) });
    if ('error' in arg && arg.error === 12006) {
      if (arg.type === Office.EventType.DialogEventReceived) {
        officeDialogRef.current = null;
        setIsDialogOpen(false);
      }
    }
  }, []);

  const openOfficeDialog = useCallback(
    (
      url: string,
      options: Office.DialogOptions,
      onMessage?: (arg: { message: string; origin: string | undefined } | { error: number }) => void
    ) => {
      dlgLog('[openDialog] opening dialog', { url, options: JSON.stringify(options) });
      if (officeDialogRef.current) {
        dlgLog('[openDialog] closing existing dialog first');
        officeDialogRef.current.close();
      }

      Office.context.ui.displayDialogAsync(url, options, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          dlgLogError('[openDialog] displayDialogAsync failed', {
            errorMessage: asyncResult.error.message,
            errorCode: asyncResult.error.code,
          });
          officeDialogRef.current = null;
          setIsDialogOpen(false);
        } else {
          dlgLog('[openDialog] dialog opened successfully');
          const dialog = asyncResult.value;
          officeDialogRef.current = dialog;
          setIsDialogOpen(true);
          try {
            if (onMessage) {
              dialog.addEventHandler(Office.EventType.DialogMessageReceived, onMessage);
            }
            dialog.addEventHandler(Office.EventType.DialogEventReceived, handleDialogEvent as any);
          } catch (handlerError) {
            dlgLogError('[openDialog] error adding event handlers', { error: String(handlerError) });
          }
        }
      });
    },
    [handleDialogEvent]
  );

  return (
    <DialogContext.Provider value={{ openOfficeDialog, isDialogOpen, closeDialog }}>{children}</DialogContext.Provider>
  );
};
